import { Controller } from "@hotwired/stimulus"
import { useTransition } from "stimulus-use"
import { saveFile } from "../../src/index"

export default class extends Controller {
  static targets = ["attachment", "slide", "imageBackground", "image", "download"]
  static values = { autoOpen: String, folderPath: String, fileNames: Object }

  eventCreated = false
  currentImage = null
  currentSlide = null
  currentIndex = null
  hidden = this.attachmentTarget.classList.contains("hidden")
  allTargetIds = this.slideTargets.map(element => { return element.id })

  initSvgSrc() {
    this.imageTargets.filter(image => image.hasAttribute("data-svg-path")).forEach(svgElement => {
      const dataUrl = svgElement.dataset.svgPath
      fetch(dataUrl)
        .then(response => response.blob())
        .then(blobText => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = function () {
              resolve("data:image/svg+xml;base64," + reader.result.split(",")[1])
            }
            reader.onerror = reject
            reader.readAsDataURL(blobText)
          })
        })
        .then(svgUrl => { svgElement.src = svgUrl })
        .catch(() => {})
    })
  }

  connect() {
    useTransition(this, { element: this.attachmentTarget })

    if (!this.hidden) {
      this.close()
    }

    this.initSvgSrc()

    if (this.hasAutoOpenValue && this.autoOpenValue) {
      this.autoOpenById(this.autoOpenValue)
    }
  }

  autoOpenById(id) {
    if (this.hasFolderPathValue) { this.restorePath = this.folderPathValue }
    this.slideTargets.forEach(element => { element.classList.add("hidden") })
    this.hidden = false
    this.toggleTransition()

    this.currentSlide = this.unhideElementById(id)
    this.currentIndex = this.allTargetIds.indexOf(this.currentSlide.id)

    this.slideChangeActions()
    this.resizeImageToFit()
    if (this.eventCreated === false) {
      const fn = this.outsideHide.bind(this)
      this.eventCreated = fn
      document.addEventListener("keydown", fn)
      document.addEventListener("click", fn)
    }
  }

  openModal(event) {
    event.preventDefault()
    if (this.hasFolderPathValue) { this.restorePath = window.location.pathname }
    this.slideTargets.forEach(element => { element.classList.add("hidden") })
    this.hidden = false
    this.toggleTransition()

    this.currentSlide = this.unhideElementById(event.params.id)
    this.currentIndex = this.allTargetIds.indexOf(this.currentSlide.id)

    this.slideChangeActions()
    this.resizeImageToFit()
    if (this.eventCreated === false) {
      const fn = this.outsideHide.bind(this)
      this.eventCreated = fn
      document.addEventListener("keydown", fn)
      document.addEventListener("click", fn)
    }
  }

  close(event) {
    this.hidden = true
    this.leave()
    this.removeEvent()
    if (this.restorePath) {
      history.replaceState(null, "", this.restorePath)
      this.restorePath = null
    }
    if (event) { event.preventDefault() }
  }

  clickOutside(event) {
    if (event.defaultPrevented) {
      return event
    } else if (!(event.target === this.currentImage) && !this.hidden) {
      this.close(event)
    }
  }

  previousSlide(event) {
    event.preventDefault()
    this.hideElement(this.currentSlide)
    const idsLength = this.allTargetIds.length
    const newIndex = this.currentIndex = (this.currentIndex <= 0 ? idsLength : this.currentIndex) - 1

    const newElement = this.unhideElementById(this.allTargetIds[newIndex])
    this.currentSlide = newElement
    this.slideChangeActions()
  }

  nextSlide(event) {
    event.preventDefault()
    this.hideElement(this.currentSlide)
    const idsLength = this.allTargetIds.length
    let newIndex = this.currentIndex + 1
    newIndex = this.currentIndex = newIndex % idsLength

    const newElement = this.unhideElementById(this.allTargetIds[newIndex])
    this.currentSlide = newElement
    this.slideChangeActions()
  }

  hideElement(oldElement) {
    oldElement = oldElement || this.slideTargets.find(el => el === this.currentSlide)
    oldElement.classList.add("hidden")
  }

  unhideElementById(id) {
    const element = this.slideTargets.find(el => el.id === id)
    element.classList.remove("hidden")
    return element
  }

  outsideHide(event) {
    const key = event.key
    if (key === "ArrowLeft") { this.previousSlide(event) }
    if (key === "ArrowRight") { this.nextSlide(event) }
    if (key === "Escape" && !this.hidden && !this.attachmentTarget.classList.contains("hidden")) {
      this.close(event)
    }
  }

  removeEvent() {
    if (this.eventCreated !== false) {
      document.removeEventListener("click", this.eventCreated)
      document.removeEventListener("keydown", this.eventCreated)
      this.eventCreated = false
    }
  }

  zoomIn(event) {
    event.preventDefault()
    if (this.currentImage.tagName === "IFRAME" || this.currentImage.tagName === "DIV") return
    this.currentImage.style.height = (this.currentImage.clientHeight + 100) + "px"
  }

  zoomOut(event) {
    event.preventDefault()
    if (this.currentImage.tagName === "IFRAME" || this.currentImage.tagName === "DIV") return
    this.currentImage.style.height = (this.currentImage.clientHeight - 100) + "px"
  }

  setCurrentImage() {
    return Array.from(this.currentSlide.querySelectorAll("*")).find(el => el.tagName === "IMG" || el.tagName === "IFRAME") ||
      this.currentSlide.querySelector("[data-file-view-target='image']")
  }

  resizeImageToFit() {
    if (!this.currentImage || this.currentImage.tagName === "IFRAME" || this.currentImage.tagName === "DIV") return

    const imageHeight = this.currentImage.offsetHeight
    const windowHeight = window.innerHeight
    if (imageHeight >= windowHeight) { this.currentImage.style.height = `${parseInt(windowHeight * 0.7)}px` }
  }

  downloadImage(event) {
    event.preventDefault()
    if (!this.currentImage || (!this.currentImage.src && !this.currentImage.dataset.downloadUrl)) return

    const filename = (this.hasFileNamesValue && this.fileNamesValue[this.currentSlide.id]) || this.currentImage.dataset.filename
    const url = this.downloadTarget.href

    fetch(url)
      .then(r => r.blob())
      .then(blob => saveFile(blob.slice(0, blob.size, blob.type), filename))
      .catch(() => {})
  }

  slideChangeActions() {
    this.currentImage = this.setCurrentImage()
    if (this.currentImage && (this.currentImage.src || this.currentImage.dataset.downloadUrl)) {
      this.downloadTarget.href = this.currentImage.dataset.downloadUrl || this.currentImage.src
    }

    if (this.hasFolderPathValue && this.hasFileNamesValue) {
      const filename = this.fileNamesValue[this.currentSlide.id]
      if (filename) {
        const folderPath = this.folderPathValue.replace(/\/$/, "")
        const deepLink = folderPath === "/drive"
          ? `/drive/f/${filename}`
          : `${folderPath}/${filename}`
        history.replaceState(null, "", deepLink)
      }
    }
  }

  disconnect() {
    this.removeEvent()
  }
}
